// ============================================================
// PERRY — BOOT SEQUENCE
// Four beats, all skippable, zero assets: the Empire bumper, the
// ember lore card, the shape of T'yphiz, and the title with its
// attract loop. Pure CSS + inline SVG — nothing loads, so nothing
// pops in half a second late.
//
// Phase machine harvested from Terror's BootSequence (pattern,
// not text): one timer chain, one skip handler, one keyframe
// injection through ensureKeyframes.
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { GM } from "./theme";
import { ensureKeyframes, EmberDust, MetalRule, CinematicLayer } from "./PerryUI";

const KF = `
@keyframes bootFade   { from { opacity: 0; } to { opacity: 1; } }
@keyframes bootRise   { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
@keyframes bootSigil  { 0% { transform: rotate(0deg) scale(0.86); opacity: 0; } 40% { opacity: 1; } 100% { transform: rotate(360deg) scale(1); opacity: 1; } }
@keyframes bootBeast  { 0% { opacity: 0; transform: scale(1.16); filter: blur(9px); } 55% { opacity: 1; filter: blur(0px); } 100% { opacity: 0.85; transform: scale(1); filter: blur(0px); } }
@keyframes bootEmber  { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
@keyframes bootTitleIn{ 0% { opacity: 0; letter-spacing: 44px; filter: blur(14px); } 100% { opacity: 1; letter-spacing: 16px; filter: blur(0); } }
@keyframes bootBlink  { 0%,100% { opacity: 1; } 50% { opacity: 0.18; } }
@keyframes bootFlicker{ 0%,100% { opacity: 0.9; } 43% { opacity: 0.7; } 47% { opacity: 1; } 62% { opacity: 0.78; } }
`;

const BEATS = [
  { id: "bumper", ms: 2600 },
  { id: "lore",   ms: 5200 },
  { id: "beast",  ms: 4600 },
  { id: "title",  ms: 0 },   // holds until a key
];

export default function BootSequence({ onStart }) {
  ensureKeyframes("perry-boot-keyframes", KF);
  const [phase, setPhase] = useState(0);

  const skip = useCallback(() => {
    setPhase((p) => (p >= BEATS.length - 1 ? p : p + 1));
  }, []);

  useEffect(() => {
    const b = BEATS[phase];
    if (!b || !b.ms) return undefined;
    const id = setTimeout(() => setPhase((p) => p + 1), b.ms);
    return () => clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat) return;                 // the guard, even here
      if (phase >= BEATS.length - 1) { onStart(); return; }
      skip();
    };
    const onClick = () => {
      if (phase >= BEATS.length - 1) onStart();
      else skip();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [phase, skip, onStart]);

  const id = BEATS[phase]?.id || "title";

  return (
    <div style={{
      position: "absolute", inset: 0, background: "#050302",
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden", cursor: "pointer", userSelect: "none",
    }}>
      <div style={{ position: "absolute", inset: 0, background: GM.candlelight }} />
      <EmberDust count={18} />

      {/* ── 1 · THE EMPIRE BUMPER ── */}
      {id === "bumper" && (
        <div style={{ textAlign: "center", animation: "bootFade 0.9s ease-out" }}>
          <div style={{ animation: "bootSigil 2.2s cubic-bezier(0.2,0.8,0.2,1) forwards", display: "inline-block" }}>
            <svg width="92" height="92" viewBox="0 0 92 92">
              <defs>
                <radialGradient id="bootSig" cx="50%" cy="42%">
                  <stop offset="0%" stopColor="#ffd9a8" />
                  <stop offset="55%" stopColor={GM.inkGoldBright} />
                  <stop offset="100%" stopColor={GM.inkGoldDim} />
                </radialGradient>
              </defs>
              <path d="M46 6 L86 46 L46 86 L6 46 Z" fill="none" stroke="url(#bootSig)" strokeWidth="2.4" />
              <path d="M46 20 L72 46 L46 72 L20 46 Z" fill="url(#bootSig)" opacity="0.85" />
              <circle cx="46" cy="46" r="7" fill="#0a0605" />
            </svg>
          </div>
          <div style={{
            marginTop: 20, fontFamily: GM.fontD, fontSize: 15, letterSpacing: 11,
            color: GM.vellumDim, textShadow: `0 0 18px ${GM.inkGoldDim}`,
          }}>THE ILLEGAL EMPIRE</div>
        </div>
      )}

      {/* ── 2 · THE LORE CARD ── */}
      {id === "lore" && (
        <div style={{
          maxWidth: 660, padding: "0 30px", textAlign: "center",
          animation: "bootRise 1.4s ease-out",
        }}>
          <div style={{
            fontFamily: GM.fontBody, fontSize: 21, lineHeight: 1.75, color: GM.vellum,
            textShadow: "0 2px 8px #000",
          }}>
            There are seven rungs in the Unstable World.
            <br />
            A <em style={{ color: GM.inkGoldBright }}>beb</em> stands on the second.
            <br />
            <span style={{ color: GM.vellumDim, fontSize: 18 }}>
              Everyone knows what a beb can and cannot do.
            </span>
          </div>
          <MetalRule width={200} style={{ margin: "26px auto 0" }} />
        </div>
      )}

      {/* ── 3 · T'YPHIZ ── */}
      {id === "beast" && (
        <div style={{ textAlign: "center" }}>
          <div style={{ animation: "bootBeast 3.4s ease-out forwards" }}>
            <svg width="330" height="230" viewBox="0 0 330 230">
              <defs>
                <linearGradient id="bootBeastG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2a0e06" />
                  <stop offset="60%" stopColor="#120806" />
                  <stop offset="100%" stopColor="#080403" />
                </linearGradient>
                <radialGradient id="bootBeastEye">
                  <stop offset="0%" stopColor="#fff4d0" />
                  <stop offset="45%" stopColor="#ffb14e" />
                  <stop offset="100%" stopColor="#d43a24" />
                </radialGradient>
              </defs>
              {/* wings */}
              <path d="M150 108 C104 62 56 52 22 74 C58 82 74 104 84 132 C104 122 130 116 150 118 Z" fill="url(#bootBeastG)" />
              <path d="M180 108 C226 62 274 52 308 74 C272 82 256 104 246 132 C226 122 200 116 180 118 Z" fill="url(#bootBeastG)" />
              {/* body */}
              <ellipse cx="165" cy="132" rx="44" ry="52" fill="url(#bootBeastG)" />
              {/* neck + head */}
              <path d="M148 96 C146 74 152 58 165 52 C178 58 184 74 182 96 Z" fill="url(#bootBeastG)" />
              <ellipse cx="165" cy="54" rx="27" ry="22" fill="url(#bootBeastG)" />
              {/* THE BILL — a duck's, and nobody is laughing */}
              <path d="M138 58 C118 60 106 66 104 72 C110 79 126 82 141 78 C146 72 144 62 138 58 Z" fill="#1c0e08" />
              <path d="M104 72 C112 76 128 79 141 76" fill="none" stroke="#3a1c10" strokeWidth="1.6" />
              {/* horns */}
              <path d="M150 38 C142 24 138 14 143 6 C150 14 156 24 158 36 Z" fill="#1c1210" />
              <path d="M180 38 C188 24 192 14 187 6 C180 14 174 24 172 36 Z" fill="#1c1210" />
              {/* the eye */}
              <ellipse cx="172" cy="52" rx="8.5" ry="7" fill="url(#bootBeastEye)" style={{ animation: "bootEmber 1.7s ease-in-out infinite" }} />
              {/* tail — pointed, yet paddled */}
              <path d="M186 168 C214 184 240 196 262 194 C250 206 224 208 200 198 Z" fill="url(#bootBeastG)" />
              <path d="M256 188 C276 180 292 186 298 198 C286 206 268 204 256 196 Z" fill="url(#bootBeastG)" />
              {/* webbed feet */}
              <path d="M142 180 C132 194 122 200 112 200 C120 208 138 206 150 196 Z" fill="#1c1210" />
              <path d="M188 180 C198 194 208 200 218 200 C210 208 192 206 180 196 Z" fill="#1c1210" />
            </svg>
          </div>
          <div style={{
            marginTop: 6, fontFamily: GM.fontD, fontSize: 26, letterSpacing: 12,
            color: "#d43a24", textShadow: "0 0 30px #d43a24, 0 2px 8px #000",
            animation: "bootFlicker 2.6s ease-in-out infinite",
          }}>T'YPHIZ</div>
          <div style={{ marginTop: 8, fontSize: 11, letterSpacing: 6, color: GM.vellumFaint }}>
            CHAMPION · THE FIRE THAT ATE REEDBROOK
          </div>
        </div>
      )}

      {/* ── 4 · TITLE + PRESS START ── */}
      {id === "title" && (
        <div style={{ textAlign: "center" }}>
          <div style={{
            fontFamily: GM.fontD, fontWeight: 700, fontSize: "clamp(62px, 13vw, 148px)",
            color: GM.vellum, letterSpacing: 16,
            textShadow: `0 0 60px ${GM.inkGold}, 0 0 18px ${GM.inkGoldBright}, 0 6px 18px #000`,
            animation: "bootTitleIn 1.8s cubic-bezier(0.2,0.8,0.2,1) forwards",
          }}>PERRY</div>
          <MetalRule width="min(420px, 70vw)" style={{ margin: "6px auto 18px" }} />
          <div style={{
            fontFamily: GM.font, fontSize: 13, letterSpacing: 7, color: GM.inkGoldBright,
            textShadow: `0 0 14px ${GM.inkGold}`,
          }}>EVERY STRIKE HAS AN ANSWER</div>
          <div style={{
            marginTop: 54, fontSize: 12, letterSpacing: 8, color: GM.vellumDim,
            animation: "bootBlink 1.5s ease-in-out infinite",
          }}>PRESS ANY KEY</div>
        </div>
      )}

      {phase < BEATS.length - 1 && (
        <div style={{
          position: "absolute", bottom: 22, right: 26,
          fontSize: 10, letterSpacing: 3, color: GM.vellumFaint, opacity: 0.7,
        }}>ANY KEY TO SKIP</div>
      )}

      <CinematicLayer bars={id !== "title"} barPx={64} zIndex={5} />
    </div>
  );
}
